import React, { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { updateEventAlerts } from "../api/events";
import { computeReminderPreview, getChannelWarning, getDefaultAlertPreferences, hasValidationErrors, normalizeOverrides, validateOverrides } from "../utils/alerts";

const alertTypeOptions = [
    { value: "slack", label: "Slack" },
    { value: "sms", label: "SMS" },
    { value: "call", label: "Call" },
];

const createEmptyRow = () => ({ type: "sms", offset: "15" });

const EventDetailsModal = ({ event, isOpen, onClose, user, token, onSaved }) => {
    const defaults = getDefaultAlertPreferences(user);
    const initialOverrides = normalizeOverrides(event?.alertOverrides);
    const [useCustom, setUseCustom] = useState(initialOverrides.length > 0);
    const [rows, setRows] = useState(initialOverrides.length > 0 ? initialOverrides : defaults);
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= 600);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const nextOverrides = normalizeOverrides(event?.alertOverrides);
        const nextUseCustom = nextOverrides.length > 0;
        setUseCustom(nextUseCustom);
        setRows(nextUseCustom ? nextOverrides : getDefaultAlertPreferences(user));
        setErrors([]);
    }, [event, isOpen, user]);

    const startTime = DateTime.fromSeconds(event?.start?.timestamp || 0);
    const endTime = DateTime.fromSeconds(event?.end?.timestamp || 0);

    const displayRows = useCustom ? rows : defaults;
    const validationErrors = useMemo(() => validateOverrides(rows), [rows]);
    const previewItems = computeReminderPreview({
        startTimestamp: event?.start?.timestamp,
        overrides: displayRows,
    });

    const handleToggle = () => {
        const nextValue = !useCustom;
        setUseCustom(nextValue);
        if (nextValue && rows.length === 0) {
            setRows(defaults.length > 0 ? defaults : [createEmptyRow()]);
        }
        if (!nextValue) {
            setErrors([]);
        }
    };

    const handleRowChange = (index, key, value) => {
        setRows(prev => {
            const next = [...prev];
            next[index] = { ...next[index], [key]: value };
            return next;
        });
    };

    const handleAddRow = () => {
        if (rows.length >= 5) {
            return;
        }
        setRows(prev => [...prev, createEmptyRow()]);
    };

    const handleRemoveRow = index => {
        setRows(prev => prev.filter((_, idx) => idx !== index));
    };

    const handleSave = async () => {
        if (!useCustom) {
            await saveOverrides([]);
            return;
        }

        const currentErrors = validateOverrides(rows);
        if (hasValidationErrors(currentErrors)) {
            setErrors(currentErrors);
            return;
        }
        setErrors([]);
        await saveOverrides(rows.map(row => ({ ...row, offset: Number(row.offset) })));
    };

    const saveOverrides = async overrides => {
        setSaving(true);
        try {
            await updateEventAlerts({ token, eventId: event.id, overrides });
            if (onSaved) {
                onSaved(overrides);
            }
            onClose();
        } catch (error) {
            alert(error.message || "Failed to update reminders.");
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <button onClick={onClose} style={styles.closeButton}>
                    ✕
                </button>
                <div style={styles.header}>
                    <h2 style={styles.title}>{event?.title || "Event"}</h2>
                    <div style={styles.subtitle}>
                        {startTime.toFormat("MMM d, yyyy")} · {startTime.toFormat("HH:mm")} - {endTime.toFormat("HH:mm")}
                    </div>
                </div>

                <div style={styles.contentArea}>
                    <div style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <span style={styles.sectionTitle}>Reminders</span>
                            <span style={styles.sectionStatus}>
                                {event?.alertOverrides?.length ? "Custom for this event" : "Using default reminders"}
                            </span>
                        </div>

                        <div style={styles.toggleRow}>
                            <label style={styles.toggleLabel}>Use custom reminders for this event</label>
                            <input type="checkbox" checked={useCustom} onChange={handleToggle} />
                        </div>

                        {errors.list && <div style={styles.errorText}>{errors.list}</div>}

                        <div style={styles.rowsContainer}>
                            {displayRows.length === 0 && !useCustom && (
                                <div style={styles.emptyState}>No defaults set. Server defaults will be used.</div>
                            )}
                            {displayRows.map((row, index) => {
                                const rowErrors = errors[index] || {};
                                const warning = getChannelWarning(row.type, user);
                                return (
                                    <div key={`${row.type}-${index}`} style={styles.row}>
                                        {useCustom && (
                                            <button style={styles.removeButton} onClick={() => handleRemoveRow(index)} aria-label="Remove reminder">
                                                <span style={styles.trashIcon}>×</span>
                                            </button>
                                        )}
                                        <div style={{ ...styles.rowMain, ...(isSmallScreen ? styles.rowMainStack : null) }}>
                                            <select
                                                style={styles.select}
                                                value={row.type}
                                                onChange={e => handleRowChange(index, "type", e.target.value)}
                                                disabled={!useCustom}
                                            >
                                                {alertTypeOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <div style={{ ...styles.offsetGroup, ...(isSmallScreen ? styles.offsetGroupStack : null) }}>
                                                <input
                                                    style={{ ...styles.input, ...(isSmallScreen ? styles.inputFull : null) }}
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    value={row.offset}
                                                    onChange={e => handleRowChange(index, "offset", e.target.value)}
                                                    disabled={!useCustom}
                                                />
                                                <span style={styles.minutesLabel}>minutes before</span>
                                            </div>
                                        </div>
                                        <div style={styles.rowMeta}>
                                            {rowErrors.offset && <span style={styles.errorText}>{rowErrors.offset}</span>}
                                            {rowErrors.type && <span style={styles.errorText}>{rowErrors.type}</span>}
                                            {rowErrors.duplicate && <span style={styles.errorText}>{rowErrors.duplicate}</span>}
                                            {warning && <span style={styles.warningText}>{warning}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {useCustom && (
                            <div style={styles.actionsRow}>
                                <button style={styles.addButton} onClick={handleAddRow} disabled={rows.length >= 5}>
                                    Add reminder
                                </button>
                            </div>
                        )}
                    </div>

                    <div style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <span style={styles.sectionTitle}>Will send at</span>
                        </div>
                        <div style={styles.previewList}>
                            {previewItems.length === 0 && <span style={styles.emptyState}>No reminders configured.</span>}
                            {previewItems.map((item, index) => {
                                const sendTime = startTime.minus({ minutes: item.offset }).toFormat("MMM d, HH:mm");
                                return (
                                    <div key={`${item.type}-${index}`} style={styles.previewRow}>
                                        <span style={styles.previewType}>{item.type.toUpperCase()}</span>
                                        <span style={styles.previewTime}>{sendTime}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div style={styles.footer}>
                    <button style={styles.secondaryButton} onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button style={styles.primaryButton} onClick={handleSave} disabled={saving || (useCustom && hasValidationErrors(validationErrors))}>
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: "1.5rem",
    },
    modal: {
        backgroundColor: "#32527E",
        color: "#ffffff",
        borderRadius: "20px",
        padding: "2rem",
        width: "100%",
        maxWidth: "640px",
        position: "relative",
        boxShadow: "0 24px 70px rgba(15, 23, 42, 0.35)",
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
    },
    closeButton: {
        position: "absolute",
        top: "16px",
        right: "16px",
        border: "none",
        background: "transparent",
        color: "#ffffff",
        fontSize: "20px",
        cursor: "pointer",
    },
    header: {
        marginBottom: "1.5rem",
    },
    title: {
        margin: 0,
        fontSize: "1.5rem",
    },
    subtitle: {
        marginTop: "0.5rem",
        color: "rgba(255,255,255,0.75)",
        fontSize: "0.9rem",
    },
    section: {
        borderTop: "1px solid rgba(255,255,255,0.2)",
        paddingTop: "1.25rem",
        marginTop: "1.25rem",
    },
    contentArea: {
        overflowY: "auto",
        paddingRight: "0.25rem",
        flex: 1,
    },
    sectionHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "1rem",
    },
    sectionTitle: {
        fontSize: "1rem",
        fontWeight: 600,
    },
    sectionStatus: {
        fontSize: "0.85rem",
        color: "rgba(255,255,255,0.7)",
    },
    toggleRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "1rem",
    },
    toggleLabel: {
        fontSize: "0.9rem",
        color: "rgba(255,255,255,0.85)",
    },
    rowsContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
    },
    row: {
        display: "flex",
        flexDirection: "column",
        gap: "0.35rem",
        background: "rgba(255,255,255,0.08)",
        padding: "0.75rem",
        borderRadius: "12px",
        border: "1px solid rgba(255,255,255,0.18)",
        position: "relative",
        paddingRight: "3rem",
    },
    rowMain: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
    },
    rowMainStack: {
        flexDirection: "column",
        alignItems: "stretch",
    },
    offsetGroup: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        flexWrap: "wrap",
    },
    offsetGroupStack: {
        justifyContent: "space-between",
    },
    rowMeta: {
        display: "flex",
        gap: "0.75rem",
        flexWrap: "wrap",
        fontSize: "0.75rem",
    },
    select: {
        padding: "0.5rem",
        borderRadius: "8px",
        border: "1px solid rgba(255,255,255,0.3)",
        background: "rgba(15, 23, 42, 0.35)",
        color: "#ffffff",
    },
    input: {
        padding: "0.5rem",
        borderRadius: "8px",
        border: "1px solid rgba(255,255,255,0.3)",
        background: "rgba(15, 23, 42, 0.35)",
        color: "#ffffff",
        width: "120px",
    },
    inputFull: {
        width: "100%",
    },
    minutesLabel: {
        fontSize: "0.8rem",
        color: "rgba(255,255,255,0.7)",
    },
    removeButton: {
        position: "absolute",
        top: "50%",
        right: "12px",
        transform: "translateY(-50%)",
        border: "none",
        background: "rgba(239, 68, 68, 0.15)",
        color: "#ef4444",
        cursor: "pointer",
        width: "26px",
        height: "26px",
        borderRadius: "8px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
    },
    trashIcon: {
        fontSize: "1rem",
        lineHeight: 1,
        fontWeight: 700,
    },
    addButton: {
        borderRadius: "10px",
        border: "1px solid rgba(255,255,255,0.3)",
        background: "rgba(255,255,255,0.1)",
        color: "#ffffff",
        padding: "0.5rem 1rem",
        cursor: "pointer",
    },
    actionsRow: {
        marginTop: "1rem",
    },
    previewList: {
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
    },
    previewRow: {
        display: "flex",
        justifyContent: "space-between",
        fontSize: "0.9rem",
        color: "#ffffff",
    },
    previewType: {
        fontWeight: 600,
    },
    previewTime: {
        color: "rgba(255,255,255,0.7)",
    },
    footer: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "0.75rem",
        marginTop: "2rem",
        position: "sticky",
        bottom: 0,
        background: "#32527E",
        paddingTop: "1rem",
    },
    primaryButton: {
        borderRadius: "11px",
        border: "none",
        background: "#ffffff",
        color: "#465C9E",
        fontSize: "12px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        fontFamily: "inherit",
        fontWeight: "400",
        height: "28px",
        width: "78px",
    },
    secondaryButton: {
        borderRadius: "11px",
        border: "1px solid rgba(255, 255, 255, 0.3)",
        background: "transparent",
        color: "white",
        fontSize: "12px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        fontFamily: "inherit",
        fontWeight: "400",
        height: "28px",
        width: "78px",
    },
    errorText: {
        color: "#fca5a5",
        fontSize: "0.75rem",
    },
    warningText: {
        color: "#fbbf24",
        fontSize: "0.75rem",
    },
    emptyState: {
        color: "rgba(255,255,255,0.7)",
        fontSize: "0.85rem",
    },
};

export default EventDetailsModal;
